import { observable, makeObservable } from 'mobx'
import Rule from './Rule'

export const Action = {
    INCLUDE: 'include',
    EXCLUDE: 'exclude',
}

class VerbatimRules {
    // This is a used for TA, TX, TL only.
    // It is neither a Rule (because it contains both include/exclude lists),
    // not a RoleGroup, because it doesn't accept operators (OR/AND/NOR)
    ruleType = 'verbatimRules'

    @observable rules = []

    existingIds = new Set()

    constructor(rules = []) {
        makeObservable(this)
        this.rules = rules.map((rule) => (rule instanceof Rule ? rule : new Rule(rule)))
        this.rules.forEach(({ options }) => {
            options.forEach(({ id }) => {
                this.existingIds.add(id)
            })
        })
    }

    static clone(verbatimRules) {
        const rules = verbatimRules.rules.slice().map((rule) => Rule.clone(rule))
        return new VerbatimRules(rules)
    }

    discard = () => {
        this.existingIds = new Set()
        this.rules = []
    }

    addRule(rule, override = false, action = Action.INCLUDE) {
        const rules = this.rules.slice()
        const existingRule = rules.find(({ id }) => id === rule.id)
        const taggedOptions = rule.options.map((option) => ({ ...option, action }))

        if (existingRule && !override) {
            const newOptions = taggedOptions.filter(({ id }) => !this.existingIds.has(id))
            if (newOptions.length === 0) return
            existingRule.options.push(...newOptions)
        } else if (existingRule && override) {
            rule.options = taggedOptions
            rules.splice(rules.indexOf(existingRule), 1, rule)
        } else {
            rule.options = taggedOptions
            rules.push(rule)
        }

        taggedOptions.forEach(({ id }) => {
            this.existingIds.add(id)
        })

        this.rules = rules
    }

    removeRule(ruleId, optionId) {
        const rules = this.rules.slice()
        const existingRule = rules.find(({ id }) => id === ruleId)

        if (!existingRule) return

        const existingOptionIndex = existingRule.options.findIndex(({ id }) => id === optionId)

        if (existingOptionIndex === -1) return

        this.existingIds.delete(optionId)
        existingRule.options.splice(existingOptionIndex, 1)

        if (existingRule.options.length === 0) {
            const existingRuleIndex = rules.findIndex(({ id }) => id === ruleId)
            rules.splice(existingRuleIndex, 1)
        }

        this.rules = rules
    }

    hasRule = (optionId) => this.existingIds.has(optionId)

    hasRules = () => this.existingIds.size > 0

    findRule = (ruleId) => this.rules.find(({ id }) => ruleId === id)

    getOptionAction = (optionId) => {
        const rule = this.rules.find(({ options }) => options.some(({ id }) => id === optionId))
        const option = rule?.options.find(({ id }) => id === optionId)
        return option?.action ?? null
    }

    isIncluded = (optionId) => this.getOptionAction(optionId) === Action.INCLUDE

    isExcluded = (optionId) => this.getOptionAction(optionId) === Action.EXCLUDE

    toJSON() {
        return {
            ruleType: this.ruleType,
            rules: this.rules.slice().map((rule) => rule.toJSON()),
        }
    }
}

export default VerbatimRules
